import Ionicons from '@expo/vector-icons/Ionicons';
import React, { useState } from 'react';
import { Pressable, View } from 'react-native';
import { KeyboardStickyView } from 'react-native-keyboard-controller';

import {
  KEYBOARD_CLEARANCE,
  KeyboardAwareScreenScroll,
} from '@/components/layout/keyboard-aware-screen-scroll';
import { Button } from '@/components/ui/button';
import { Text } from '@/components/ui/text';
import { useAuth } from '@/hooks/use-auth';
import { useUserLocation } from '@/hooks/use-user-location';
import { FriendPickerSheet } from '@/features/friend-recommendations/components/friend-picker-sheet';
import { SaveToListSheet } from '@/features/lists/components/save-to-list-sheet';

import { NoteEntryCard } from '../../notes/components/note-entry-card';
import { NoteFooter } from '../../notes/components/note-footer';
import { useCheckInActions, type PendingRetry } from '../hooks/use-check-in-actions';
import { useCheckInSuccess } from '../hooks/use-check-in-success';
import { useDrafts, type CheckInDraft } from '../hooks/use-drafts';
import { useCheckInEntries } from '../hooks/use-check-in-entries';
import { useCheckInFormState, MAX_ENTRIES, MAX_NOTE_ENTRIES } from '../hooks/use-check-in-form-state';
import { useCheckInMode } from '../hooks/use-check-in-mode';
import { BusinessSearchSection } from './business-search-section';
import { CheckInEntryCard } from './check-in-entry-card';
import { CheckInFooter } from './check-in-footer';
import { CheckInHeader } from './check-in-header';
import { CheckInSuccessScreen } from './check-in-success-screen';
import { DateSection } from './date-section';
import { DiningTypeSection } from './dining-type-section';
import { ModeToggle } from './mode-toggle';

function countOf(count: number, word: string): string {
  return `${count} ${word}${count === 1 ? '' : 's'}`;
}

/** The banner text for a submit that left photos or items behind. */
export function getRetryMessage({ mode, failedPhotoCount, failedItemCount }: PendingRetry): string {
  const itemWord = mode === 'note' ? 'note' : 'check-in';
  const parts: string[] = [];
  if (failedItemCount > 0) {
    parts.push(`${countOf(failedItemCount, itemWord)} didn't ${mode === 'note' ? 'save' : 'post'}.`);
  }
  if (failedPhotoCount > 0) {
    parts.push(`${countOf(failedPhotoCount, 'photo')} didn't upload.`);
    if (failedItemCount === 0) {
      parts.push(mode === 'note' ? 'Your note was saved.' : 'Your check-in was posted.');
    }
  }
  return parts.join(' ');
}

interface RetryBannerProps {
  pendingRetry: PendingRetry;
  isSubmitting: boolean;
  onRetry: () => void;
  onDiscard: () => void;
}

/**
 * Shown above the footer after a submit that did not fully succeed. The
 * failed photos keep their error overlays above; Retry uploads only what is
 * missing, Discard leaves without it.
 */
function RetryBanner({ pendingRetry, isSubmitting, onRetry, onDiscard }: RetryBannerProps) {
  return (
    <View
      className="mx-4 mb-2 rounded-lg border border-destructive/40 bg-surface px-3 py-2"
      accessibilityRole="alert"
      accessibilityLiveRegion="polite"
    >
      <Text className="text-sm text-foreground">{getRetryMessage(pendingRetry)}</Text>
      <View className="mt-2 flex-row justify-end gap-2">
        <Button
          variant="outline"
          size="sm"
          onPress={onDiscard}
          disabled={isSubmitting}
          accessibilityLabel="Discard failed uploads"
        >
          Discard
        </Button>
        <Button
          variant="primary"
          size="sm"
          onPress={onRetry}
          loading={isSubmitting}
          accessibilityLabel="Retry failed uploads"
        >
          Retry
        </Button>
      </View>
    </View>
  );
}

export interface QuickCheckInPageProps {
  initialDraft?: CheckInDraft | null;
  initialBusinessId?: string;
  initialMenuItemId?: string;
}

export function QuickCheckInPage({
  initialDraft,
  initialBusinessId,
  initialMenuItemId,
}: QuickCheckInPageProps) {
  const { coords } = useUserLocation();
  const { saveDraft, deleteDraft } = useDrafts();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const {
    state,
    setState,
    noteEntries,
    setNoteEntries,
    isSubmitting,
    setIsSubmitting,
    isSavingDraft,
    setIsSavingDraft,
    draftIdRef,
    setLastSavedState,
    readyEntries,
    hasUnsavedChanges,
    canSubmit,
    readyNoteEntries,
    canSubmitNotes,
    handleSetBusiness,
    handleClearBusiness,
    handleSetDiningType,
    handleSetOccurredAt,
  } = useCheckInFormState({ initialDraft, initialBusinessId });

  const {
    handleUpdateEntry,
    handleRemoveEntry,
    handleAddEntry,
    handleUpdateNoteEntry,
    handleRemoveNoteEntry,
    handleAddNoteEntry,
  } = useCheckInEntries({ setState, setNoteEntries });

  const success = useCheckInSuccess();

  // The footer rides above the keyboard on a sticky view, so a focused input has
  // to clear the keyboard AND the footer. Measure the footer and feed its height
  // into the scroll view's bottomOffset instead of guessing at a magic number.
  const [footerHeight, setFooterHeight] = useState(0);

  const { mode, handleModeChange } = useCheckInMode({
    entries: state.entries,
    noteEntries,
    setState,
    setNoteEntries,
  });

  const {
    handleSaveDraft,
    handleClose,
    handleSubmit,
    handleSaveNotes,
    pendingRetry,
    handleRetry,
    handleDiscardFailed,
  } = useCheckInActions({
    state,
    hasUnsavedChanges,
    canSubmit,
    canSubmitNotes,
    readyEntries,
    readyNoteEntries,
    draftIdRef,
    setIsSubmitting,
    setIsSavingDraft,
    setLastSavedState,
    setNoteEntries,
    setState,
    coords,
    saveDraft,
    deleteDraft,
    onSubmitSuccess: success.showSuccess,
  });

  if (success.visible && success.data) {
    return (
      <View className="flex-1">
        <CheckInSuccessScreen
          data={success.data}
          onDismiss={success.dismiss}
          onSaveToList={() => success.setSaveSheetVisible(true)}
          onRecommend={() => success.setFriendPickerVisible(true)}
        />
        <SaveToListSheet
          itemType="menu_item"
          itemId={success.data.menuItemId}
          visible={success.saveSheetVisible}
          onClose={() => success.setSaveSheetVisible(false)}
        />
        <FriendPickerSheet
          visible={success.friendPickerVisible}
          onClose={() => success.setFriendPickerVisible(false)}
          onConfirm={success.handleSendRecommendations}
          menuItemId={success.data.menuItemId}
        />
      </View>
    );
  }

  return (
    <View className="flex-1">
      <CheckInHeader
        onClose={handleClose}
        onSaveDraft={handleSaveDraft}
        isSavingDraft={isSavingDraft}
        hasUnsavedChanges={hasUnsavedChanges}
      />

      <View style={{ flex: 1 }}>
        {!state.business ? (
          <View style={{ flex: 1, paddingHorizontal: 16, paddingTop: 12 }}>
            <BusinessSearchSection
              selectedBusiness={state.business}
              onSelect={handleSetBusiness}
              onClear={handleClearBusiness}
              fillVertical
            />
          </View>
        ) : (
          <KeyboardAwareScreenScroll
            className="flex-1 px-4"
            bottomOffset={footerHeight + KEYBOARD_CLEARANCE}
            contentContainerStyle={{ paddingTop: 12, paddingBottom: 24 }}
          >
            <BusinessSearchSection
              selectedBusiness={state.business}
              onSelect={handleSetBusiness}
              onClear={handleClearBusiness}
            />

            <ModeToggle
              mode={mode}
              onChange={handleModeChange}
              disabled={isSubmitting}
            />

            {mode === 'check-in' && (
              <>
                <DiningTypeSection
                  value={state.diningType}
                  onChange={handleSetDiningType}
                  disabled={isSubmitting}
                />

                {isAdmin && (
                  <DateSection
                    value={state.occurredAt}
                    onChange={handleSetOccurredAt}
                    disabled={isSubmitting}
                  />
                )}
              </>
            )}

            <View className="my-3 h-px bg-border/50" />

            {mode === 'check-in' &&
              state.entries.map((entry, index) => (
                <CheckInEntryCard
                  key={entry.id}
                  entry={entry}
                  entryIndex={index}
                  businessId={state.business!.id}
                  businessName={state.business!.name}
                  isSubmitting={isSubmitting}
                  canRemove={state.entries.length > 1}
                  onUpdate={handleUpdateEntry}
                  onRemove={handleRemoveEntry}
                />
              ))}

            {mode === 'check-in' && state.entries.length < MAX_ENTRIES && (
              <View className="items-center mt-1 mb-4">
                <Pressable
                  onPress={handleAddEntry}
                  disabled={isSubmitting}
                  accessibilityLabel="Add another item"
                  className="w-10 h-10 rounded-full border border-border items-center justify-center bg-surface active:bg-muted"
                >
                  <Ionicons name="add" size={22} className="text-foreground" />
                </Pressable>
                <Text className="text-xs text-muted-foreground mt-1">
                  Add another item
                </Text>
              </View>
            )}

            {mode === 'note' &&
              noteEntries.map((entry, index) => (
                <NoteEntryCard
                  key={entry.id}
                  entry={entry}
                  entryIndex={index}
                  businessId={state.business!.id}
                  businessName={state.business!.name}
                  isSubmitting={isSubmitting}
                  canRemove={noteEntries.length > 1}
                  onUpdate={handleUpdateNoteEntry}
                  onRemove={handleRemoveNoteEntry}
                />
              ))}

            {mode === 'note' && noteEntries.length < MAX_NOTE_ENTRIES && (
              <View className="items-center mt-1 mb-4">
                <Pressable
                  onPress={handleAddNoteEntry}
                  disabled={isSubmitting}
                  accessibilityLabel="Add another note"
                  className="w-10 h-10 rounded-full border border-border items-center justify-center bg-surface active:bg-muted"
                >
                  <Ionicons name="add" size={22} className="text-foreground" />
                </Pressable>
                <Text className="text-xs text-muted-foreground mt-1">
                  Add another note
                </Text>
              </View>
            )}
          </KeyboardAwareScreenScroll>
        )}

        {/* Footer rides on a KeyboardStickyView so the primary CTA lifts to sit
            just above the keyboard instead of being covered by it. */}
        <KeyboardStickyView
          onLayout={(e) => setFooterHeight(e.nativeEvent.layout.height)}
        >
          {pendingRetry && (
            <RetryBanner
              pendingRetry={pendingRetry}
              isSubmitting={isSubmitting}
              onRetry={() => void handleRetry()}
              onDiscard={() => void handleDiscardFailed()}
            />
          )}
          {mode === 'check-in' ? (
            <CheckInFooter
              onSubmit={handleSubmit}
              isEnabled={canSubmit}
              isSubmitting={isSubmitting}
              entryCount={readyEntries.length}
            />
          ) : (
            <NoteFooter
              onSubmit={handleSaveNotes}
              isEnabled={canSubmitNotes}
              isSubmitting={isSubmitting}
              entryCount={readyNoteEntries.length}
            />
          )}
        </KeyboardStickyView>
      </View>
    </View>
  );
}

export default QuickCheckInPage;
